import { useState, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, FileText, CheckCircle2, AlertCircle, Loader2, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/utils";

const formatCurrency = (v) => {
  if (v == null) return "—";
  if (v >= 1e7) return `₹${(v/1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v/1e5).toFixed(2)} L`;
  if (v >= 1e3) return `₹${(v/1e3).toFixed(1)} K`;
  return `₹${v.toLocaleString("en-IN")}`;
};

// Per-file status: pending | uploading | success | duplicate | error
const initialEntry = (file) => ({
  file,
  status: "pending",
  result: null,
  error: null,
});

/**
 * Bulk-friendly Online Invoice Upload modal.
 * Accepts multiple PDFs, uploads them sequentially, and reports per-file results.
 */
export default function OnlineInvoiceUploadModal({ open, onClose, onSuccess }) {
  const [entries, setEntries] = useState([]); // [{file, status, result, error}]
  const [isUploading, setIsUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const reset = () => { setEntries([]); setIsUploading(false); };

  const addFiles = (fileList) => {
    const newOnes = Array.from(fileList || [])
      .filter(f => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"))
      .map(initialEntry);
    if (newOnes.length === 0) {
      toast.error("Only PDF files are supported");
      return;
    }
    setEntries(prev => [...prev, ...newOnes]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const removeEntry = (i) => setEntries(prev => prev.filter((_, idx) => idx !== i));

  const uploadAll = async () => {
    if (entries.length === 0 || isUploading) return;
    setIsUploading(true);
    let createdCount = 0;
    let duplicateCount = 0;
    let errorCount = 0;

    for (let i = 0; i < entries.length; i++) {
      const ent = entries[i];
      if (ent.status === "success" || ent.status === "duplicate") continue;

      setEntries(prev => prev.map((e, idx) => idx === i ? { ...e, status: "uploading", error: null } : e));
      try {
        const form = new FormData();
        form.append("file", ent.file);
        const res = await axios.post(`${API}/quotations/online-invoice-upload`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        });
        setEntries(prev => prev.map((e, idx) => idx === i ? { ...e, status: "success", result: res.data } : e));
        createdCount += 1;
      } catch (err) {
        const status = err?.response?.status;
        const detail = extractErrorMessage(err, "Upload failed");
        if (status === 409) {
          setEntries(prev => prev.map((e, idx) => idx === i ? { ...e, status: "duplicate", error: detail } : e));
          duplicateCount += 1;
        } else {
          setEntries(prev => prev.map((e, idx) => idx === i ? { ...e, status: "error", error: detail } : e));
          errorCount += 1;
        }
      }
    }
    setIsUploading(false);
    if (createdCount > 0) {
      toast.success(`${createdCount} invoice${createdCount > 1 ? "s" : ""} imported successfully`);
      onSuccess?.();
    }
    if (duplicateCount > 0) toast.warning(`${duplicateCount} duplicate invoice${duplicateCount > 1 ? "s" : ""} skipped`);
    if (errorCount > 0) toast.error(`${errorCount} invoice${errorCount > 1 ? "s" : ""} failed`);
  };

  const closeAndReset = () => {
    if (isUploading) return;
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeAndReset(); }}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[85vh] flex flex-col overflow-hidden p-0" data-testid="online-invoice-upload-modal">
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-[#E8E8E8]">
          <DialogTitle className="text-base font-semibold text-[#0F0F0F] flex items-center gap-2">
            <FileText className="h-4 w-4 text-accent" /> Upload Online Invoice
          </DialogTitle>
          <p className="text-[11px] text-muted-foreground">
            Drop one or more PDF invoices. We'll auto-create Quotations Log entries with Type = Online.
          </p>
        </DialogHeader>

        <div className="px-5 py-3 flex-1 overflow-auto space-y-3">
          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
            className={`border-2 border-dashed rounded-md px-4 py-6 text-center cursor-pointer transition ${
              dragOver ? "border-accent bg-accent/5" : "border-[#E8E8E8] hover:bg-slate-50"
            }`}
            data-testid="online-invoice-dropzone"
          >
            <Upload className={`h-7 w-7 mx-auto mb-2 ${dragOver ? "text-accent" : "text-slate-400"}`} />
            <p className="text-sm font-medium text-slate-700">
              {dragOver ? "Drop PDF invoices here…" : "Click to browse or drag PDFs here"}
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">PDF only · multiple files supported</p>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,.pdf"
              multiple
              hidden
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
              data-testid="online-invoice-file-input"
            />
          </div>

          {/* File list */}
          {entries.length > 0 && (
            <div className="space-y-1.5" data-testid="online-invoice-entries">
              {entries.map((ent, i) => (
                <FileRow key={`${ent.file.name}-${i}`} entry={ent} index={i} onRemove={removeEntry} disabled={isUploading} />
              ))}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-[#E8E8E8] flex items-center justify-between bg-slate-50/50">
          <div className="text-[11px] text-muted-foreground">
            {entries.length > 0 && `${entries.length} file${entries.length > 1 ? "s" : ""} queued`}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={closeAndReset} disabled={isUploading} data-testid="online-invoice-close">
              Close
            </Button>
            <Button
              size="sm"
              className="bg-accent hover:bg-accent/90"
              onClick={uploadAll}
              disabled={entries.length === 0 || isUploading}
              data-testid="online-invoice-upload-btn"
            >
              {isUploading ? (<><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Uploading…</>) : (<>Upload {entries.length > 0 ? `(${entries.length})` : ""}</>)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FileRow({ entry, index, onRemove, disabled }) {
  const { file, status, result, error } = entry;
  const icon = status === "uploading" ? <Loader2 className="h-3.5 w-3.5 text-blue-500 animate-spin" />
    : status === "success" ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
    : status === "duplicate" ? <RefreshCw className="h-3.5 w-3.5 text-amber-500" />
    : status === "error" ? <AlertCircle className="h-3.5 w-3.5 text-red-500" />
    : <FileText className="h-3.5 w-3.5 text-slate-400" />;
  const subtitle = status === "success" && result
    ? `${result.quotation_id} · ${result.seller_name} · ${formatCurrency(result.total_invoice_value)} · ${result.items_count} items${result.unmapped_skus?.length ? ` · ${result.unmapped_skus.length} unmapped SKU${result.unmapped_skus.length > 1 ? "s" : ""}` : ""}`
    : status === "duplicate" ? error
    : status === "error" ? error
    : `${(file.size/1024).toFixed(1)} KB`;
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-white border border-[#E8E8E8] rounded-md text-xs" data-testid={`online-invoice-row-${index}`}>
      {icon}
      <div className="flex-1 min-w-0">
        <p className="text-slate-800 truncate font-medium">{file.name}</p>
        <p className={`text-[10.5px] truncate ${
          status === "success" ? "text-emerald-600"
          : status === "duplicate" ? "text-amber-600"
          : status === "error" ? "text-red-600"
          : "text-muted-foreground"
        }`}>{subtitle}</p>
      </div>
      {status === "pending" && !disabled && (
        <button onClick={() => onRemove(index)} className="text-slate-400 hover:text-red-500" data-testid={`online-invoice-remove-${index}`}>
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
